import { BN } from 'fbonds-core'
import { BANX_ADVENTURE_GAP } from 'fbonds-core/lib/fbond-protocol/constants'
import {
  calculateAdventureRewards,
  getHistoryAdventureStakeUserInfo,
} from 'fbonds-core/lib/fbond-protocol/functions/banxStaking/banxTokenStaking'
import moment from 'moment'

import { staking } from '@banx/api/common'
import { BANX_TOKEN_DECIMALS } from '@banx/constants'
import { ZERO_BN, bnToFixed, bnToHuman } from '@banx/utils'

export const calcPartnerPoints = (
  tokensAmount: BN,
  tokensPerPartnerPoints: BN | undefined,
): number => {
  if (!tokensPerPartnerPoints) return 0

  const tokensAmountValue = bnToHuman(tokensAmount, BANX_TOKEN_DECIMALS)
  const tokensPerPartnerValue = bnToHuman(tokensPerPartnerPoints, BANX_TOKEN_DECIMALS)

  if (tokensPerPartnerValue === 0) {
    return 0
  }

  const partnerPoints = tokensAmountValue / tokensPerPartnerValue
  return isNaN(partnerPoints) ? 0 : partnerPoints
}

export const banxTokenBNToFixed = (value: BN, fractionDigits = 2) =>
  bnToFixed({ value, fractionDigits, decimals: BANX_TOKEN_DECIMALS })

//? Adventure status helpers
export const checkIsUserStaking = (banxTokenStake: staking.BanxTokenStake) => {
  const { tokensStaked, banxNftsStakedQuantity } = banxTokenStake
  return !tokensStaked.isZero() || banxNftsStakedQuantity.gt(ZERO_BN)
}

export const isAdventureStarted = (adventure: staking.BanxAdventure): boolean => {
  return adventure.periodStartedAt.toNumber() + BANX_ADVENTURE_GAP < moment().unix()
}

export const isAdventureUpcomming = (adventure: staking.BanxAdventure): boolean => {
  const isEnded = isAdventureEnded(adventure)
  const isLive = isAdventureLive(adventure)

  return !isEnded && !isLive
}

export const isAdventureLive = (adventure: staking.BanxAdventure): boolean => {
  const isStarted = isAdventureStarted(adventure)
  const isEnded = isAdventureEnded(adventure)

  return isStarted && !isEnded
}

export const isAdventureEnded = (adventure: staking.BanxAdventure): boolean => {
  const endTime = getAdventureEndTime(adventure)
  return endTime < moment().unix()
}

export const getAdventureEndTime = (adventure: staking.BanxAdventure): number => {
  const isStarted = isAdventureStarted(adventure)

  return isStarted
    ? adventure.periodEndingAt.toNumber()
    : adventure.periodStartedAt.toNumber() + BANX_ADVENTURE_GAP
}

export const getAdventureStatus = (adventure: staking.BanxAdventure): staking.AdventureStatus => {
  const isEnded = isAdventureEnded(adventure)
  const isLive = isAdventureLive(adventure)

  if (isEnded) {
    return staking.AdventureStatus.ENDED
  }

  if (isLive) {
    return staking.AdventureStatus.LIVE
  }

  return staking.AdventureStatus.UPCOMING
}

//? Calculate rewards that can still be claimed from ended adventures
export const getClaimableRewards = (props: {
  adventures: staking.BanxAdventure[]
  subscription: staking.BanxAdventureSubscription | null
}): BN => {
  const { adventures, subscription } = props

  if (!subscription || !adventures.length) return ZERO_BN

  const unclaimedEndedAdventures = adventures.filter(
    (adventure) => isAdventureEnded(adventure) && adventure.week.gt(subscription.lastClaimedWeek),
  )

  return calculateAdventureRewards({ adventure: unclaimedEndedAdventures, subscription })
}

//? Calculate total rewards already claimed from ended adventures
export const getTotalClaimedRewards = (props: {
  adventures: staking.BanxAdventure[]
  subscriptionSnapshots: staking.BanxAdventureSnapshot[]
}): BN => {
  const { adventures, subscriptionSnapshots } = props

  if (!subscriptionSnapshots.length || !adventures.length) return ZERO_BN

  return adventures.filter(isAdventureEnded).reduce((total, adventure) => {
    const snapshot = subscriptionSnapshots.find((s) => s.week.eq(adventure.week))

    const rewardsClaimed = snapshot
      ? getHistoryAdventureStakeUserInfo({ adventure, snapshot }).rewardsClaimed
      : ZERO_BN

    return total.add(rewardsClaimed)
  }, ZERO_BN)
}
